import React, { FC, useCallback } from 'react';
import Button from 'src/common/components/Button';
import Modal from 'src/common/components/Modal';
import apiFetch from 'src/pubsub/apiFetch';
import { toast } from 'react-toastify';
import Notification from 'src/common/components/Notification';
import unpackApiError from 'src/common/utils/unpackApiError';
import s from './ReportMessageModal.module.scss';

type ReportMessageModalProps = {
  isOpen: boolean;
  closeModal: () => void;
  threadId: number;
  messageId: number;
  disableReportButton: () => void;
};

const DEFAULT_ERROR_MESSAGE = 'Something went wrong. Please try again.';

const showNotification = (
  heading: string,
  type?: 'error',
) => {
  toast.dismiss();
  toast(
    <Notification heading={heading} />,
    { autoClose: 5000, type },
  );
};

const ReportMessageModal: FC<ReportMessageModalProps> = ({
  isOpen,
  closeModal,
  threadId,
  messageId,
  disableReportButton,
}) => {
  const reportMessage = useCallback(async () => {
    try {
      const response = await apiFetch(
        '/support/report-message/',
        {
          method: 'POST',
          body: JSON.stringify({
            thread_id: threadId,
            message_id: messageId,
          }),
        },
      );
      if (!response.ok) {
        const error = await unpackApiError(response);
        showNotification(error?.detail || DEFAULT_ERROR_MESSAGE, 'error');
      }
      if (response.ok) {
        showNotification('Message reported successfully.');
        disableReportButton();
        closeModal();
      }
    } catch (error) {
      showNotification(DEFAULT_ERROR_MESSAGE, 'error');
    }
  }, [threadId, messageId, closeModal, disableReportButton]);

  return (
    <Modal
      name="reportMessage"
      label="Report Message"
      modalOpen={isOpen}
      closeModal={closeModal}
      classNameModifier="white"
    >
      <div className={s.content}>
        <div className={s.body}>
          <h2 className={s.title}>Report Message</h2>
          <p className={s.description}>
            You’re about to report this message as possible spam.
            Our team will review it to help keep your inbox safe. Do you want to continue?
          </p>
        </div>
        <div className={s.footer}>
          <Button
            type="button"
            classNameModifier="secondary noMargin"
            onClick={closeModal}
            fullWidth
          >
            Cancel
          </Button>
          <Button
            type="button"
            classNameModifier="noMargin"
            onClick={reportMessage}
            fullWidth
          >
            Report
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ReportMessageModal;
